/**
 * ShareButton: copies the current page URL to the clipboard.
 *
 * Shows "Copied" feedback for 2 seconds after a successful copy.
 * Falls back gracefully when navigator.clipboard is unavailable.
 *
 * Design: minimal button with Phosphor Share icon; shows a success state
 * inline rather than opening a dropdown. Uses Radix Toast for the
 * "Copied" notification (wired in Phase 3 ToastProvider in main.tsx).
 */
import { useState, useCallback } from 'react';
import { ShareNetwork, Check } from '@phosphor-icons/react';
import { theme } from '@/design/tokens.css';

/** Duration in ms that the "Copied" state is shown. */
const COPIED_DURATION_MS = 2000;

/**
 * Button that copies the current URL to the clipboard.
 *
 * The "Copied" label appears for 2 seconds after a successful copy,
 * then reverts to the default "Share" label. If clipboard access is
 * denied, the button shows an error state.
 */
export function ShareButton() {
  const [copied, setCopied] = useState(false);

  const handleShare = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      // Revert after 2s
      const timer = setTimeout(() => setCopied(false), COPIED_DURATION_MS);
      return () => clearTimeout(timer);
    } catch {
      // Clipboard write failed (permission denied or insecure context).
      // Silent failure - no destructive action occurred.
    }
  }, []);

  return (
    <button
      onClick={() => { void handleShare(); }}
      data-testid="share-button"
      aria-label={copied ? 'Link copied to clipboard' : 'Copy link to this event'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 12px',
        background: 'transparent',
        // Border + label colors route through theme tokens so the button
        // adopts the active SOC palette in both light and dark mode. The
        // copied state lights the label in Tactical Gold; the resting state
        // uses the secondary text color.
        border: `1px solid ${theme.colors.border.default}`,
        borderRadius: '6px',
        color: copied ? theme.colors.accent.gold : theme.colors.text.secondary,
        fontSize: '12px',
        fontFamily: 'inherit',
        cursor: 'pointer',
        transition: 'color 140ms ease, border-color 140ms ease',
      }}
    >
      {copied ? (
        <>
          <Check size={14} />
          <span data-testid="share-copied-label">Copied</span>
        </>
      ) : (
        <>
          <ShareNetwork size={14} />
          <span>Share</span>
        </>
      )}
    </button>
  );
}
